import { Facet, RangeSetBuilder, StateEffect, type Extension, type Text } from "@codemirror/state";
import { Decoration, EditorView, ViewPlugin, type DecorationSet, type ViewUpdate } from "@codemirror/view";
import type { ProjectIndex } from "../lib/api";

// ---------------------------------------------------------------- worker client

/** true if correct, false if misspelt, or this language's spelling of a word spelt the way another region spells it. */
type Verdict = boolean | string;

class SpellClient {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, (v: unknown) => void>();
  cache = new Map<string, Verdict>();
  private listeners = new Set<() => void>();
  ready = false;

  constructor(readonly lang: string, words: string[]) {
    this.worker = new Worker(new URL("./spell.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (e) => {
      const m = e.data;
      if (m.type === "ready") {
        this.ready = true;
        this.emit();
      } else if (m.type === "error") {
        // Without a dictionary, flag nothing rather than everything.
        console.warn("[spell]", m.message);
      } else if (m.type === "checked") {
        const wrong = m.wrong as Record<string, false | string>;
        for (const w of m.words as string[]) this.cache.set(w, Object.hasOwn(wrong, w) ? wrong[w] : true);
        this.pending.get(m.id)?.(null);
        this.pending.delete(m.id);
        this.emit();
      } else if (m.type === "suggestions") {
        this.pending.get(m.id)?.(m.suggestions);
        this.pending.delete(m.id);
      }
    };
    this.worker.postMessage({ type: "init", lang, words });
    for (const w of words) this.cache.set(w, true);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }
  onChange(fn: () => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  check(words: string[]) {
    const unknown = words.filter((w) => !this.cache.has(w));
    if (unknown.length === 0) return;
    for (const w of unknown) this.cache.set(w, true); // optimistic until the worker answers
    const id = this.nextId++;
    this.pending.set(id, () => {});
    this.worker.postMessage({ type: "check", id, words: unknown });
  }

  suggest(word: string): Promise<string[]> {
    const id = this.nextId++;
    return new Promise((resolve) => {
      this.pending.set(id, (v) => resolve(v as string[]));
      this.worker.postMessage({ type: "suggest", id, word });
    });
  }

  add(word: string) {
    this.cache.set(word, true);
    this.cache.set(`${word}-`, true);
    this.worker.postMessage({ type: "add", word });
    this.emit();
  }

  destroy() {
    this.worker.terminate();
  }
}

const clients = new Map<string, SpellClient>();

/** One shared worker per language. */
export function spellClient(lang: string, words: string[]): SpellClient {
  let c = clients.get(lang);
  if (!c) clients.set(lang, (c = new SpellClient(lang, words)));
  return c;
}

/** What each language calls its own spelling and the one it flags. */
const REGIONS: Record<string, { local: string; other: string }> = {
  "en-AU": { local: "Australian", other: "American" },
  "en-GB": { local: "British", other: "American" },
  "en-US": { local: "American", other: "British" },
};

// ---------------------------------------------------------------- what to check

const MATH_ENVS = new Set([
  "equation", "equation*", "align", "align*", "alignat", "alignat*", "gather", "gather*", "multline", "multline*",
  "eqnarray", "eqnarray*", "displaymath", "math", "flalign", "flalign*", "verbatim", "verbatim*", "lstlisting", "minted",
  "comment", "tikzpicture", "axis", "Verbatim", "filecontents", "filecontents*", "split", "cases", "Verbatim*", "BVerbatim",
  "LVerbatim", "spverbatim", "IEEEeqnarray", "IEEEeqnarray*", "dmath", "dmath*", "pgfpicture", "circuitikz", "luacode", "luacode*",
]);

const SKIP_ARG_CMDS = new Set([
  "label", "ref", "eqref", "pageref", "autoref", "cref", "Cref", "vref", "nameref", "cite", "citep", "citet", "autocite",
  "parencite", "textcite", "footcite", "nocite", "citeauthor", "citeyear", "usepackage", "RequirePackage", "documentclass",
  "begin", "end", "includegraphics", "input", "include", "includeonly", "subfile", "url", "href", "bibliography",
  "bibliographystyle", "addbibresource", "newcommand", "renewcommand", "providecommand", "newenvironment", "def", "color",
  "textcolor", "colorbox", "definecolor", "hypersetup", "setlength", "addtolength", "setcounter", "vspace", "hspace",
  "SI", "si", "qty", "unit", "num", "ang", "gls", "Gls", "glspl", "Glspl", "acrshort", "acrlong", "acrfull", "ac", "acs",
  "acl", "acf", "newacronym", "newglossaryentry", "lstinputlisting", "inputminted", "mintinline", "lstset", "pgfplotsset",
  "tikzset", "usetikzlibrary", "geometry", "graphicspath", "includepdf", "includesvg", "bibitem", "fontspec", "setmainfont",
  "setsansfont", "setmonofont", "pagestyle", "thispagestyle", "pagenumbering", "renewenvironment", "newtheorem",
  "multicolumn", "multirow", "cline", "fancyhead", "fancyfoot", "rowcolor", "cellcolor", "texttt", "path", "verb",
  "rowcolors", "colorlet", "addcontentsline", "addtocounter", "newcounter", "newcolumntype", "DeclareMathOperator",
  "DeclareSIUnit", "sisetup", "captionsetup", "cmidrule", "specialrule", "addlinespace", "printbibliography", "printglossary",
  "hyperref",
]);

/** How many {..} arguments those commands take before any prose; the rest take one. */
const ARG_COUNTS: Record<string, number> = {
  newcommand: 2, renewcommand: 2, providecommand: 2, definecolor: 3, rowcolors: 3, newenvironment: 3, renewenvironment: 3,
  newacronym: 2, multicolumn: 2, multirow: 2, setlength: 2, addtolength: 2, setcounter: 2, addtocounter: 2, addcontentsline: 2,
  colorlet: 2, newcolumntype: 2, DeclareMathOperator: 2, DeclareSIUnit: 2, crefrange: 2, Crefrange: 2, hyperref: 0,
};

/** Arguments after \begin{env} that aren't prose, like a table's column spec or a minipage's width. */
const ENV_ARGS: Record<string, number> = {
  tabular: 1, "tabular*": 2, tabularx: 2, tabulary: 2, xltabular: 2, longtable: 1, "longtable*": 1, supertabular: 1, array: 1,
  tabu: 1, longtabu: 1, NiceTabular: 1, minipage: 1, subfigure: 1, subtable: 1, wrapfigure: 2, wraptable: 2, multicols: 1,
  "multicols*": 1, adjustbox: 1,
};

/** Commands named like citations, references, labels and glossary entries take keys, even ones a project defines itself. */
function takesKeys(name: string): boolean {
  return SKIP_ARG_CMDS.has(name) || /cite/i.test(name) || /ref$/i.test(name) || /label$/.test(name) || /^(?:gls|Gls|GLS|acr|Acr)/.test(name);
}

/** A letter written with a TeX accent or as a special letter: \"u, \'{e}, {\o}, \c{c}, \ae, \ss. */
const TEX_LETTER_INNER = String.raw`\\(?:[\x60'^"~=.](?:\{(?:\\[ij]|[a-zA-Z])\}|\\[ij](?![a-zA-Z])|[a-zA-Z])|[cvuHkdbr]\{(?:\\[ij]|[a-zA-Z])\}|(?:ae|AE|oe|OE|aa|AA|o|O|l|L|ss)(?![a-zA-Z])(?:\{\}| (?=[a-zA-Z]))?)`;
const TEX_LETTER = String.raw`(?:\{${TEX_LETTER_INNER}\}|${TEX_LETTER_INNER})`;
const TEX_LETTER_AT = new RegExp(TEX_LETTER, "y");
const LETTER = String.raw`(?:\p{L}|${TEX_LETTER})`;
const WORD = new RegExp(String.raw`${LETTER}(?:${LETTER}|['’](?=${LETTER}))*`, "gu");

const COMBINING: Record<string, string> = {
  "`": "̀", "'": "́", "^": "̂", '"': "̈", "~": "̃", "=": "̄", ".": "̇",
  c: "̧", v: "̌", u: "̆", H: "̋", k: "̨", r: "̊", d: "̣", b: "̱",
};
const SPECIAL: Record<string, string> = { ae: "æ", AE: "Æ", oe: "œ", OE: "Œ", aa: "å", AA: "Å", o: "ø", O: "Ø", l: "ł", L: "Ł", ss: "ß" };

/** Turns TeX accents into the letters they make: Br\"uel → Brüel, Kj\ae r → Kjær. */
export function untex(raw: string): string {
  if (!raw.includes("\\")) return raw;
  return raw
    .replace(/\\([\x60'^"~=.cvuHkdbr])\{?\\?([a-zA-Z])\}?/g, (_, accent: string, ch: string) => ch + COMBINING[accent])
    .replace(/\\(ae|AE|oe|OE|aa|AA|o|O|l|L|ss)(?![a-zA-Z])(?:\{\}| )?/g, (_, s: string) => SPECIAL[s])
    .replace(/[{}]/g, "")
    .normalize("NFC");
}

const URL_OR_EMAIL = /\b(?:https?:\/\/|www\.)[^\s{}\\]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;

/** Ranges of the doc that are code rather than prose. */
export function ignoredRanges(doc: Text): Array<[number, number]> {
  const text = doc.toString();
  const out: Array<[number, number]> = [];
  const n = text.length;
  let i = 0;
  const skipGroup = (start: number, open: string, close: string): number => {
    let depth = 0;
    for (let k = start; k < n; k++) {
      const ch = text[k];
      if (ch === "\\") {
        k++;
        continue;
      }
      if (ch === open) depth++;
      else if (ch === close && --depth === 0) return k + 1;
    }
    return n;
  };
  /** Skips {..} groups (and [..] options between them) from pos; returns where they end. */
  const skipArgs = (pos: number, groups: number, parens = false): number => {
    let end = pos;
    while (end < n) {
      while (end < n && /\s/.test(text[end]) && text[end] !== "\n") end++;
      if (text[end] === "[") end = skipGroup(end, "[", "]");
      else if (text[end] === "(" && parens) end = skipGroup(end, "(", ")");
      else if (text[end] === "{" && groups > 0) {
        end = skipGroup(end, "{", "}");
        groups--;
      } else break;
    }
    return end;
  };
  while (i < n) {
    const ch = text[i];
    if (ch === "%") {
      const end = text.indexOf("\n", i);
      out.push([i, end < 0 ? n : end]);
      i = end < 0 ? n : end;
    } else if (ch === "$") {
      const display = text[i + 1] === "$";
      const close = display ? "$$" : "$";
      let k = i + close.length;
      while (k < n && !(text.startsWith(close, k) && text[k - 1] !== "\\")) k++;
      out.push([i, Math.min(n, k + close.length)]);
      i = k + close.length;
    } else if (ch === "\\") {
      // Accented letters are part of the word around them.
      TEX_LETTER_AT.lastIndex = i;
      const accent = TEX_LETTER_AT.exec(text);
      if (accent) {
        i += accent[0].length;
        continue;
      }
      const next = text[i + 1];
      if (next === "(" || next === "[") {
        const close = next === "(" ? "\\)" : "\\]";
        const end = text.indexOf(close, i + 2);
        const stop = end < 0 ? n : end + 2;
        out.push([i, stop]);
        i = stop;
        continue;
      }
      const m = /^\\([a-zA-Z@]+)\*?/.exec(text.slice(i, i + 60));
      if (!m) {
        out.push([i, i + 2]);
        i += 2;
        continue;
      }
      const name = m[1];
      let end = i + m[0].length;
      if (name === "begin") {
        const env = /^\{([^}]*)\}/.exec(text.slice(end, end + 60));
        if (env && MATH_ENVS.has(env[1])) {
          const close = text.indexOf(`\\end{${env[1]}}`, end);
          const stop = close < 0 ? n : close + 6 + env[1].length;
          out.push([i, stop]);
          i = stop;
          continue;
        }
        end = skipArgs(end, 1 + (env ? (ENV_ARGS[env[1]] ?? 0) : 0));
      } else if (name === "verb" || name === "lstinline" || name === "mintinline") {
        // \verb|code|, \lstinline[style]{code}, \mintinline{lang}|code|
        end = skipArgs(end, name === "mintinline" ? 1 : 0);
        if (text[end] === "{") end = skipGroup(end, "{", "}");
        else if (end < n) {
          const close = text.indexOf(text[end], end + 1);
          end = close < 0 ? n : close + 1;
        }
      } else if (takesKeys(name)) {
        end = skipArgs(end, ARG_COUNTS[name] ?? 1, name === "cmidrule");
      } else {
        // Key=value options like \tikzexternalize[prefix=...] are code; \item[Label] stays prose.
        while (text[end] === "[") {
          const close = skipGroup(end, "[", "]");
          if (!text.slice(end, close).includes("=")) break;
          end = close;
        }
      }
      out.push([i, end]);
      i = end;
    } else i++;
  }
  for (const m of text.matchAll(URL_OR_EMAIL)) out.push([m.index, m.index + m[0].length]);
  return out.sort((a, b) => a[0] - b[0]);
}

interface Word {
  from: number;
  to: number;
  /** The word as the dictionary should see it. */
  word: string;
  /** What to look up: the word, or the word and a hyphen when it starts a hyphenated compound like thermo-viscous. */
  key: string;
}

function wordsIn(text: string, offset: number, ignored: Array<[number, number]>, out: Word[]) {
  let j = 0;
  for (const m of text.matchAll(WORD)) {
    const from = offset + m.index;
    let to = from + m[0].length;
    while (j < ignored.length && ignored[j][1] <= from) j++;
    if (j < ignored.length && ignored[j][0] < to) continue;
    // Words run into numbers, file names or addresses: 11th, 90mm, fig_2, user@host.
    const before = text[m.index - 1] ?? "";
    const after = text[m.index + m[0].length] ?? "";
    if (/[\d_@]/.test(before) || /[\d_@]/.test(after)) continue;
    let word = untex(m[0]).replace(/’/g, "'");
    // Possessives and contractions are checked without the 's: Biot's, Resonate's, it's.
    if (/'s$/i.test(word)) {
      word = word.slice(0, -2);
      to -= 2;
    }
    if ([...word].length < 2) continue;
    if (/^\p{Lu}+s?$/u.test(word) && /\p{Lu}{2}/u.test(word)) continue; // acronyms, and their plurals
    if (/\p{Ll}\p{Lu}/u.test(word)) continue; // product names and units like SpaceClaim, kHz, dB
    const hyphenated = after === "-" && /\p{L}/u.test(text[m.index + m[0].length + 1] ?? "");
    out.push({ from, to, word, key: hyphenated ? `${word}-` : word });
  }
}

/** Every word of a whole doc that spell check looks at. */
export function proseWords(doc: Text): Word[] {
  const out: Word[] = [];
  wordsIn(doc.toString(), 0, ignoredRanges(doc), out);
  return out;
}

/** Surnames from the project's bibliography, which are names rather than misspellings wherever they appear. */
const namesCache = new WeakMap<ProjectIndex, Set<string>>();
export function bibliographyNames(index: ProjectIndex | null): Set<string> {
  if (!index) return new Set();
  let names = namesCache.get(index);
  if (!names) {
    names = new Set();
    for (const c of index.citations) for (const m of untex(c.author ?? "").matchAll(/\p{L}[\p{L}'’]+/gu)) names.add(m[0]);
    namesCache.set(index, names);
  }
  return names;
}

// ---------------------------------------------------------------- extension

interface SpellConfig {
  lang: string;
  words: string[];
  getIndex?: () => ProjectIndex | null;
}

export const spellLang = Facet.define<SpellConfig | null, SpellConfig | null>({
  combine: (v) => v[v.length - 1] ?? null,
});

const spellRefresh = StateEffect.define<null>();

const misspeltMark = (word: string) => Decoration.mark({ class: "cm-misspelt", word });
const regionalMark = (word: string, local: string, lang: string) => {
  const region = REGIONS[lang];
  return Decoration.mark({
    class: "cm-spelling-variant",
    attributes: region ? { title: `${region.other} spelling. ${region.local}: ${local}` } : {},
    word,
    local,
  });
};

const spellPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet = Decoration.none;
    private client: SpellClient | null;
    private ignored: Array<[number, number]> | null = null;
    private timer: number | undefined;
    private off: (() => void) | null = null;
    private indexWaits = 0;

    constructor(private view: EditorView) {
      const conf = view.state.facet(spellLang);
      this.client = conf ? spellClient(conf.lang, conf.words) : null;
      if (this.client) this.off = this.client.onChange(() => this.schedule(50));
      this.schedule(100);
    }

    update(update: ViewUpdate) {
      if (update.docChanged) {
        this.ignored = null;
        this.decorations = this.decorations.map(update.changes);
        this.schedule(600);
      } else if (update.viewportChanged) {
        this.schedule(150);
      }
    }

    private schedule(ms: number) {
      clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.run(), ms);
    }

    private run() {
      if (!this.client?.ready) return;
      const { state } = this.view;
      const conf = state.facet(spellLang);
      const index = conf?.getIndex?.() ?? null;
      // Bibliography names arrive with the project index; look again once it has loaded.
      if (conf?.getIndex && !index && this.indexWaits++ < 5) this.schedule(2000);
      const names = bibliographyNames(index);
      this.ignored ??= ignoredRanges(state.doc);
      const words: Word[] = [];
      for (const { from, to } of this.view.visibleRanges) {
        wordsIn(state.sliceDoc(from, to), from, this.ignored, words);
      }
      this.client.check([...new Set(words.map((w) => w.key))]);
      const builder = new RangeSetBuilder<Decoration>();
      for (const w of words) {
        if (names.has(w.word)) continue;
        const verdict = this.client.cache.get(w.key);
        if (verdict === false) builder.add(w.from, w.to, misspeltMark(w.word));
        else if (typeof verdict === "string") builder.add(w.from, w.to, regionalMark(w.word, verdict, this.client.lang));
      }
      this.decorations = builder.finish();
      this.view.dispatch({ effects: spellRefresh.of(null) });
    }

    destroy() {
      clearTimeout(this.timer);
      this.off?.();
    }
  },
  { decorations: (v) => v.decorations },
);

export function spellcheck(lang: string | null, words: string[], getIndex?: () => ProjectIndex | null): Extension {
  if (!lang) return [];
  return [spellLang.of({ lang, words, getIndex }), spellPlugin];
}

export interface SpellingHit {
  from: number;
  to: number;
  word: string;
  /** For another region's spelling, this language's spelling and what to call it. */
  regional?: { spelling: string; label: string };
}

/** The flagged word at a position, if any (for the context menu). */
export function misspeltAt(view: EditorView, pos: number): SpellingHit | null {
  const conf = view.state.facet(spellLang);
  const decorations = view.plugin(spellPlugin)?.decorations;
  if (!conf || !decorations) return null;
  let hit: SpellingHit | null = null;
  decorations.between(pos, pos, (from, to, deco) => {
    const { word, local } = deco.spec as { word: string; local?: string };
    hit = { from, to, word };
    if (local) hit.regional = { spelling: local, label: REGIONS[conf.lang]?.local ?? "" };
    return false;
  });
  return hit;
}

export function suggestFor(view: EditorView, word: string): Promise<string[]> {
  const conf = view.state.facet(spellLang);
  if (!conf) return Promise.resolve([]);
  return spellClient(conf.lang, conf.words).suggest(word);
}

export function learnWord(view: EditorView, word: string) {
  const conf = view.state.facet(spellLang);
  if (!conf) return;
  spellClient(conf.lang, conf.words).add(word);
}
